import { List } from 'immutable'
import React, { ReactElement, useState } from 'react'
import { Link } from 'react-router'

import { addAlertSignature } from '../../actions/alerts'
import paths from '../../constants/paths'
import Company from '../../model/company'
import CompanyUser from '../../model/companyUser'
import Invoice from '../../model/invoice'
import StripeConfiguration, { StripeConfigurationSetup } from '../../model/stripeConfiguration'
import Transfer, { TransferType } from '../../model/transfer'
import { CompanyPaymentIntegrationReducer } from '../../reducers/companyPaymentIntegrations'
import { FormComponentProps, withValidations } from '../../utils/form-utils'
import { formatTransferType } from '../../utils/format-utils'
import { formatNumber } from '../../utils/number-utils'
import { t, tx } from '../../utils/translation-utils'
import StripeIntegrationModal from '../companies-single/stripe/StripeIntegrationModal'
import Button from '../elements/button'
import Card from '../elements/card'
import Checkbox from '../elements/checkbox'
import Col from '../elements/grid/col'
import Row from '../elements/grid/row'
import Input from '../elements/input'
import Modal from '../elements/modal'
import Subtitle from '../elements/Subtitle'
import DumbLink from '../widgets/DumbLink'

type Props = {
  approvers: List<CompanyUser>
  hasDeadline: boolean
  neededApprovers: number
  companyPaymentIntegrations: CompanyPaymentIntegrationReducer
  invoices: List<Invoice>
  company: Company
  isOverDeadline?: boolean
  hasNETSIntegration?: boolean
  isManualPayroll?: boolean
  transfers: List<Transfer>

  closeModal: () => void
  addAlert: addAlertSignature
  addStripeConfiguration: () => Promise<StripeConfigurationSetup | void>
  updateStripeConfiguration: (setupIntentID: string) => Promise<StripeConfiguration | void>
  onApproveManually?: () => void
  onApproveAutomatically?: () => void
}

type Fields = {
  users: { [key: string]: boolean }
  approvalCode?: string
}

export type ApproveResult = {
  userIDs: string[]
  approvalCode?: string
}

function ApprovePayRoll(props: Props & FormComponentProps<Fields, ApproveResult>): ReactElement | null {
  const [modalKey, setModalKey] = useState(1)
  const [showStripe, setShowStripe] = useState(false)
  // DISABLED: Confirmation modal for late approval buttons
  // const [showConfirmation, setShowConfirmation] = useState(false)
  // const [confirmationType, setConfirmationType] = useState<'manual' | 'automatic' | null>(null)

  const setStripeVisibility = (showStripe: boolean) => {
    // Increment modalKey to create a new component
    setModalKey(modalKey + 1)
    setShowStripe(showStripe)
  }

  const getManualTransferTypes = (): string => {
    if (!props.transfers || props.transfers.size === 0) {
      return ''
    }
    return props.transfers
      .reduce((transfers: { type: TransferType; manual: number; total: number }[], transfer) => {
        const idx = transfers.findIndex((t) => t.type === transfer.type)
        if (idx === -1) {
          transfers.push({
            type: transfer.type,
            total: 1,
            manual: transfer.paymentMethod === 'Manual' ? 1 : 0,
          })
        } else {
          transfers[idx].total += 1
          if (transfer.paymentMethod === 'Manual') {
            transfers[idx].manual += 1
          }
        }
        return transfers
      }, [])
      .filter((type) => type.manual > 0)
      .map((type) => {
        if (type.manual !== type.total) {
          return t('pay_roll.single.warnings.warning.manual_transfers.out_of_format', {
            type: formatTransferType(type.type),
            manual: formatNumber(type.manual),
            total: formatNumber(type.total),
          })
        }
        return formatTransferType(type.type)
      })
      .sort((a, b) => a.localeCompare(b))
      .join(', ')
  }

  const hasPaymentIntegration = () => {
    return props.companyPaymentIntegrations.companyPaymentIntegrations.some(
      (v) => (v.paymentIntegrationType === 'Stripe' || v.paymentIntegrationType === 'NETS') && v.status !== 'Expired'
    )
  }

  const invoiceAmount = () => {
    const invoice = props.invoices.first()
    return invoice ? invoice.total || 0 : 0
  }

  if ((!hasPaymentIntegration() || props.company.isPayrollLocked) && invoiceAmount() > 0) {
    return (
      <Card className="pay-roll-approve">
        {props.company.isPayrollLocked && (
          <>
            <Subtitle>{t('pay_roll.single.approve.payroll_locked.title')}</Subtitle>
            <p>{t('pay_roll.single.approve.payroll_locked.intro.line_1')}</p>
            <p>
              {tx('pay_roll.single.approve.payroll_locked.intro.line_2', {
                link: (
                  <Link to={'/' + paths.COMPANIES + '/' + props.company.id + '/invoices'}>
                    {t('pay_roll.single.approve.payroll_locked.intro.line_2.link')}
                  </Link>
                ),
              })}
            </p>
          </>
        )}
        {!props.company.isPayrollLocked && !hasPaymentIntegration() && (
          <>
            <Subtitle>{t('pay_roll.single.approve.no_payment_integration.title')}</Subtitle>
            <p>{t('pay_roll.single.approve.no_payment_integration.intro.line_1')}</p>
            <p>{t('pay_roll.single.approve.no_payment_integration.intro.line_2')}</p>
          </>
        )}
        <DumbLink
          style={{ marginTop: '15px', float: 'right' }}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault()
            setStripeVisibility(true)
          }}
        >
          <Button type="primary" className="gtm-add-stripe-integration">
            {t('pay_roll.single.approve.create_stripe_integration_button')}
          </Button>
        </DumbLink>

        <Modal
          key={modalKey}
          visible={showStripe}
          onOk={() => setStripeVisibility(false)}
          onCancel={() => setStripeVisibility(false)}
          width={376}
          footer={null}
        >
          <StripeIntegrationModal
            visible={showStripe}
            hasIntegration={false}
            company={props.company}
            closeModal={() => {
              setStripeVisibility(false)
              props.closeModal()
            }}
            addAlert={props.addAlert}
            addStripeConfiguration={props.addStripeConfiguration}
            updateStripeConfiguration={props.updateStripeConfiguration}
          />
        </Modal>
      </Card>
    )
  }

  const { decorateField, decorateAnyField } = props
  const approvers = props.approvers

  // DISABLED: Handler functions for late approval buttons confirmation modal
  // const handleManualApproval = () => {
  //   setConfirmationType('manual')
  //   setShowConfirmation(true)
  // }

  // const handleAutomaticApproval = () => {
  //   setConfirmationType('automatic')
  //   setShowConfirmation(true)
  // }

  // const handleConfirm = () => {
  //   setShowConfirmation(false)
  //   if (confirmationType === 'manual' && props.onApproveManually) {
  //     props.onApproveManually()
  //   } else if (confirmationType === 'automatic' && props.onApproveAutomatically) {
  //     props.onApproveAutomatically()
  //   }
  //   setConfirmationType(null)
  // }

  // const handleCancelConfirmation = () => {
  //   setShowConfirmation(false)
  //   setConfirmationType(null)
  // }

  return (
    <>
      <Card className="pay-roll-approve" style={props.isManualPayroll ? { width: '500px' } : { width: '450px' }}>
        <Subtitle style={{ fontSize: '20px', color: 'var(--sally-text)' }}>
          {props.neededApprovers === 1
            ? t('pay_roll.single.approve.title.finally')
            : t('pay_roll.single.approve.title.partial')}
        </Subtitle>
        <p className="approve-intro">
          {props.isOverDeadline && props.hasNETSIntegration
            ? t('pay_roll.single.approve.intro_dealine')
            : t('pay_roll.single.approve.intro')}
          {!props.hasDeadline && <>{t('pay_roll.single.approve.intro.no_deadline')}</>}
        </p>
        {props.neededApprovers > 1 && (
          <p>{t('pay_roll.single.approve.partial_approval', { count: props.neededApprovers - 1 })}</p>
        )}
        {props.neededApprovers > 1 && approvers.size > 0 && (
          <Row>
            <Col span={24}>
              <p>{t('pay_roll.single.approve.select_other_approvers')}</p>
              {approvers.map((approver) => {
                return decorateAnyField('users.' + approver.userID, { valueOnChecked: true })(
                  <Checkbox id={'users.' + approver.userID} key={'users.' + approver.userID}>
                    {approver.name}
                  </Checkbox>
                )
              })}
            </Col>
          </Row>
        )}
        {props.company.askForPayrollApprovalCode && (
          <Row className="payroll-approve-deadline approval-code-row">
            <Col span={24}>
              {decorateField('approvalCode', {
                placeholder: t('pay_roll.single.approve.approval_code'),
              })(<Input />)}
            </Col>
          </Row>
        )}

        {/* Below is approval options */}
        {/* DISABLED: Late approval buttons (manual/automatic choice after deadline)
            This feature is temporarily disabled. When enabled, it shows two options after deadline:
            - Manual approval: Process transfers manually after deadline
            - Automatic approval: Process via NETS even after deadline
            
            To re-enable: 
            1. Uncomment the block below
            2. Update the conditional to check: props.isOverDeadline && props.hasNETSIntegration
            3. Adjust the following conditions accordingly
        */}
        {/* {props.isOverDeadline && props.hasNETSIntegration ? (
          <Col span={4} className="approval-options-container">
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div className="approval-options-wrapper">
                <div className="approval-option-box manual">
                  <Subtitle style={{ fontWeight: 'bold' }}>{t('pay_roll.single.approve.manual_title')}</Subtitle>
                  <p className="approval-option-description">
                    {t('pay_roll.single.approve.manual_description_line_1')}
                  </p>
                  <p className="approval-option-description with-bottom-margin">
                    {t('pay_roll.single.approve.manual_description_line_2')}
                  </p>
                  <p className="approval-option-description with-bottom-margin">
                    <Link
                      target="_blank"
                      to="https://help.salary.dk/da/articles/1176343-manuelle-overforsler-efter-lonkorsel"
                    >
                      {t('pay_roll.single.approve.manual_description_link')}
                    </Link>
                  </p>

                  <Button
                    onClick={handleManualApproval}
                    size="extra-extra-large"
                    type="secondary"
                    danger
                    className="approval-option-button"
                  >
                    {t('pay_roll.single.approve.manual_button')}
                  </Button>
                </div>
                <div className="approval-option-box automatic">
                  <Subtitle style={{ fontWeight: 'bold' }}>{t('pay_roll.single.approve.automatic_title')}</Subtitle>
                  <p className="approval-option-description with-bottom-margin">
                    {t('pay_roll.single.approve.automatic_description_line_1')}
                  </p>

                  <p className="approval-option-description">
                    <span className="approval-option-note">
                      {t('pay_roll.single.approve.automatic_description_note')}
                    </span>
                    {t('pay_roll.single.approve.automatic_description_line_2')}
                  </p>
                  <Button
                    onClick={handleAutomaticApproval}
                    size="extra-extra-large"
                    type="primary"
                    className="approval-option-button automatic"
                  >
                    {t('pay_roll.single.approve.automatic_button')}
                  </Button>
                </div>
              </div>
            </div>
          </Col>
        ) : */}
        {props.isManualPayroll ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: '2em',
              alignItems: 'center',
              gap: '1.5em',
            }}
          >
            <div className="manual-only-reminder" style={{ width: '450px', padding: '.5em' }}>
              <div style={{ width: '450px' }}>
                <Subtitle style={{ fontSize: '16px', marginBottom: '0.5em', fontWeight: 'bolder' }}>
                  {t('pay_roll.single.approve.manual_transfers_title')}
                </Subtitle>
                <p style={{ fontSize: '14px', color: 'var(--sally-text)', marginBottom: '1em', fontWeight: 'bold' }}>
                  {t('pay_roll.single.warnings.warning.manual_transfers.line_3')} {getManualTransferTypes()}
                </p>
                <p style={{ fontSize: '14px', color: 'var(--sally-text)', marginBottom: '0.5em' }}>
                  {t('pay_roll.single.approve.manual_transfers_description')}
                </p>

                <Link
                  target="_blank"
                  to="https://help.salary.dk/da/articles/1176343-manuelle-overforsler-efter-lonkorsel"
                  style={{ fontSize: '14px', color: 'var(--ageras-blue)', textDecoration: 'underline' }}
                >
                  {t('pay_roll.single.approve.manual_transfers_guide_link')}
                </Link>
              </div>
            </div>

            <Button htmlType="submit" size="extra-large" type="primary">
              {t('pay_roll.single.approve.submit')}
            </Button>
          </div>
        ) : (
          <div style={{ marginTop: '2em', textAlign: 'center' }}>
            <Button htmlType="submit" size="extra-large" type="primary">
              {t('pay_roll.single.approve.submit')}
            </Button>
          </div>
        )}
      </Card>

      {/* DISABLED: Confirmation Modal for late approval buttons
      <Modal
        visible={showConfirmation}
        onOk={handleConfirm}
        onCancel={handleCancelConfirmation}
        width={500}
        footer={null}
        className="approval-confirmation-modal"
      >
        <Card className="confirmation-card">
          <Subtitle style={{ fontSize: '18px', marginBottom: '1em' }}>
            {confirmationType === 'manual'
              ? t('pay_roll.single.approve.confirm_manual_title')
              : t('pay_roll.single.approve.confirm_automatic_title')}
          </Subtitle>

          <p className="confirmation-description">
            {confirmationType === 'manual'
              ? t('pay_roll.single.approve.confirm_manual_description')
              : t('pay_roll.single.approve.confirm_automatic_description')}
          </p>

          <div className="confirmation-buttons">
            <Button size="extra-large" type="secondary" onClick={handleCancelConfirmation}>
              {t('general.cancel')}
            </Button>
            <Button
              size="extra-large"
              type={confirmationType === 'manual' ? 'secondary' : 'primary'}
              danger={confirmationType === 'manual'}
              onClick={handleConfirm}
            >
              {t('pay_roll.single.approve.confirm_button')}
            </Button>
          </div>
        </Card>
      </Modal>
      */}
    </>
  )
}

export default withValidations<Props, Fields, ApproveResult>({
  mapPropsToFields: (props) => {
    const fields: Fields = {
      users: {},
    }
    // only pre-select approvers if there is only one
    const selected = props.approvers.size < 2
    props.approvers.forEach((approver) => {
      fields.users[approver.userID] = selected
    })
    return fields
  },
  onSubmit: (values) => {
    const userIDs: string[] = Object.keys(values.users).filter((key) => values.users[key])
    return {
      userIDs,
      approvalCode: values.approvalCode,
    }
  },
})(ApprovePayRoll)
